'use client';

import React from 'react';
import styled from '@emotion/styled';
import { Grid, Typography, Link as MuiLink, Avatar } from '@mui/material';
import ExperienceCard from '@/components/cards/experience-card/experience-card';
import { UserFieldsFragment } from '@/generated/graphql';
import Image from 'next/image';
import { urlFor } from '@/utils/buildImageSource';
import { useExperiencesByUserLazyQuery } from '@/generated/graphql';
import extractUserId from '@/utils/extractUserId';
import { CircularProgress, Box } from '@mui/material';
import { config } from '@/config/environment';

const SlideCard = styled.div`
  background: rgba(59, 25, 50, 0.45);
  border-radius: 12px;
  box-shadow: 0 6px 18px rgba(198, 153, 187, 0.2);
  padding: 20px;
  width: 70%;
  min-height: 640px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  @media (max-width: 900px) {
    width: 90%;
    padding: 16px;
    min-height: 560px;
  }
  @media (max-width: 600px) {
    width: 100%;
    padding: 12px;
    min-height: auto;
  }
`;

const SlideContent = styled.div`
  width: 100%;
  max-width: 1100px;
`;

const NameRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  @media (max-width: 600px) {
    flex-direction: column;
    gap: 8px;
    justify-content: center;
    align-items: center;
    text-align: center;
  }
`;

const Description = styled(Typography)`
  color: white;
  font-size: 18px;
  @media (max-width: 600px) {
    font-size: 15px;
  }
`;

const Title = styled(Typography)`
  font-size: 22px;
  margin-bottom: 50px;
  font-weight: 700;
  @media (max-width: 600px) {
    font-size: 18px;
    margin-bottom: 24px;
  }
`;

export type OurCoachSlideProps = {
  coach?: UserFieldsFragment;
};

export const OurCoachSlide = ({ coach }: OurCoachSlideProps): React.ReactElement => {
  const experienceEdges = coach?.experiences?.edges;
  const initialExperiences =
    experienceEdges && experienceEdges.length ? experienceEdges.map(e => e.node) : [];

  // Helper to extract a userId from a profileLink like: https://.../<userId>
  const extractUserId = (profileLink?: string): string | null => {
    if (!profileLink) return null;
    try {
      const u = new URL(profileLink);
      return u.hostname;
    } catch {
      const segments = profileLink.split('/').filter(Boolean);
      return segments.length ? segments[segments.length - 1] : null;
    }
  };

  const profileLink = (coach as any)?.profileLink || (coach as any)?.coach?.profileLink;
  const userId = extractUserId(profileLink) || undefined;

  // Use generated Apollo lazy hook to fetch experiences for the user when we have a userId
  const [loadExperiences, { called, loading, data, error }] = useExperiencesByUserLazyQuery({
    fetchPolicy: 'cache-first',
  });

  React.useEffect(() => {
    if (userId) {
      loadExperiences({ variables: { userId: userId as any } });
    }
  }, [userId, loadExperiences]);

  const fetchedExperiences = data?.user?.experiences?.edges?.map((e: any) => e.node) ?? null;
  const experiences = fetchedExperiences ?? initialExperiences;
  // show up to 3 experiences; if fewer, center them and make a single item larger
  const toShow = experiences.filter((e: any) => e.status === 'Publish').slice(0, 3);
  const justifyExperiences: 'center' | 'flex-start' = toShow.length < 3 ? 'center' : 'flex-start';

  const avatarSrc = (coach as any)?.image
    ? urlFor((coach as any).image)
    : (coach as any)?.profile?.avatar?.url || '';

  const archiveMode = config.platformMode === 'archive';

  return (
    <SlideCard>
      <SlideContent>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <Avatar
              src={avatarSrc}
              alt={(coach as any)?.name || 'coach'}
              sx={{
                width: { xs: 84, sm: 100, md: 168 },
                height: { xs: 84, sm: 100, md: 168 },
                borderRadius: '50%',
                '& img': { objectFit: 'cover' },
                display: 'block',
                margin: { xs: '0 auto', sm: 'initial' },
                border: '3px solid white',
              }}
            />
          </Grid>
          <Grid item xs={12} sm={9}>
            <div>
              <NameRow>
                <Typography
                  variant="subtitle1"
                  component="div"
                  sx={{ fontSize: { xs: '24px', sm: '35px' } }}
                >
                  {(coach as any)?.name}
                </Typography>
                {!archiveMode && (
                  <MuiLink
                    href={`/profile/${extractUserId((coach as any)?.profileLink || (coach as any)?.coach?.profileLink)}`}
                    underline="always"
                    sx={{
                      color: 'white',
                      fontStyle: 'italic',
                      textDecorationColor: 'white',
                      textAlign: { xs: 'center', sm: 'right' },
                      display: 'block',
                    }}
                  >
                    See profile
                  </MuiLink>
                )}
              </NameRow>
              <Description variant="body2">
                {(coach as any)?.bio || (coach as any)?.headline || ''}
              </Description>
            </div>
          </Grid>
        </Grid>
      </SlideContent>

      {!archiveMode && (
        <SlideContent>
          <Title>Experiences I host</Title>
          <Grid container spacing={2} justifyContent={justifyExperiences}>
            {toShow.map((exp, idx) => (
              <Grid
                key={exp?.id ?? `exp-${idx}`}
                item
                xs={12}
                sm={toShow.length === 1 ? 6 : 4}
                sx={{ display: { xs: idx === 0 ? 'block' : 'none', sm: 'block' } }}
              >
                <ExperienceCard experience={exp} />
              </Grid>
            ))}
          </Grid>
        </SlideContent>
      )}
    </SlideCard>
  );
};

export default OurCoachSlide;
